import { toQueryParams, mapPaginatedResponse, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  Carrier,
  CarrierCascadeMode,
  CarrierEnabledScope,
  CarrierType,
  CreateCarrier,
  PaginatedResponse,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  SortParams,
  UpdateCarrier,
} from '@klareospace/pharos-lib-types';

import type {
  CarrierSortableColumn,
  NationalCarrierSortableColumn,
  SiteCarrierSortableColumn,
} from '../types/carrier.types';

// ─── Types ───────────────────────────────────────────────────────────────────

type AnyCarrierSortableColumn = NationalCarrierSortableColumn | CarrierSortableColumn | SiteCarrierSortableColumn;

export type CarrierSearchResult = PaginatedResponse<Carrier>;

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchCarriersParams extends PaginationParams {
  enabledOnSiteId?: string;
  integrations?: boolean[];
  notEnabledOnSiteId?: string;
  search?: string;
  sort?: SortParams<AnyCarrierSortableColumn>;
  spaceId?: string;
  types?: CarrierType[];
  unreferencedInSpaceId?: string;
}

export interface ReferenceCarrierRequest {
  carrierId: string;
  enabledScopes?: CarrierEnabledScope[];
  spaceId: string;
}

export interface UnreferenceCarrierRequest {
  carrierId: string;
  cascadeMode?: CarrierCascadeMode;
  spaceId: string;
}

export interface EnableOnSiteRequest {
  carrierId: string;
  from?: Date | null;
  siteId: string;
  spaceId: string;
  to?: Date | null;
}

export interface DisableOnSiteRequest {
  carrierId: string;
  siteId: string;
  spaceId: string;
}

export interface UpdateSiteEnablementRequest {
  carrierId: string;
  from?: Date | null;
  siteId: string;
  spaceId: string;
  to?: Date | null;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createCarrierApi(client: ApiClient): {
  archiveCarrier: (id: string, cascadeMode?: CarrierCascadeMode) => Promise<Carrier>;
  createCarrier: (body: CreateCarrier) => Promise<Carrier>;
  disableOnSite: (request: DisableOnSiteRequest) => Promise<Carrier>;
  enableOnSite: (request: EnableOnSiteRequest) => Promise<Carrier>;
  getCarrier: (id: string) => Promise<Carrier>;
  referenceCarrier: (request: ReferenceCarrierRequest) => Promise<Carrier>;
  searchCarriers: (params: SearchCarriersParams) => Promise<CarrierSearchResult>;
  unreferenceCarrier: (request: UnreferenceCarrierRequest) => Promise<Carrier>;
  updateCarrier: (id: string, body: UpdateCarrier) => Promise<Carrier>;
  updateSiteEnablement: (request: UpdateSiteEnablementRequest) => Promise<Carrier>;
} {
  return {
    archiveCarrier: async (id: string, cascadeMode?: CarrierCascadeMode): Promise<Carrier> => {
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        cascadeMode
          ? client.instance.delete(`/carriers/${id}`, { params: { cascadeMode } })
          : client.instance.delete(`/carriers/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    createCarrier: async (body: CreateCarrier): Promise<Carrier> => {
      const response = await client.execute<PharosApiResponse<Carrier>>(() => client.instance.post('/carriers', body));
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    disableOnSite: async (request: DisableOnSiteRequest): Promise<Carrier> => {
      const { carrierId, siteId, spaceId } = request;
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        client.instance.patch(`/carriers/${carrierId}/disable-on-site`, { siteId, spaceId }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    enableOnSite: async (request: EnableOnSiteRequest): Promise<Carrier> => {
      const { carrierId, from, siteId, spaceId, to } = request;
      const body: Record<string, unknown> = { siteId, spaceId, from, to };
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        client.instance.patch(`/carriers/${carrierId}/enable-on-site`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    getCarrier: async (id: string): Promise<Carrier> => {
      const response = await client.execute<PharosApiResponse<Carrier>>(() => client.instance.get(`/carriers/${id}`));
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    referenceCarrier: async (request: ReferenceCarrierRequest): Promise<Carrier> => {
      const body: Record<string, unknown> = {};
      if (request.enabledScopes && request.enabledScopes.length > 0) body.enabledScopes = request.enabledScopes;
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        client.instance.patch(`/carriers/${request.carrierId}/reference/${request.spaceId}`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchCarriers: async (params: SearchCarriersParams): Promise<CarrierSearchResult> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<Carrier>>>(() =>
        client.instance.post('/carriers/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    unreferenceCarrier: async (request: UnreferenceCarrierRequest): Promise<Carrier> => {
      const url = `/carriers/${request.carrierId}/unreference/${request.spaceId}`;
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        request.cascadeMode
          ? client.instance.patch(url, { cascadeMode: request.cascadeMode })
          : client.instance.patch(url),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateCarrier: async (id: string, body: UpdateCarrier): Promise<Carrier> => {
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        client.instance.patch(`/carriers/${id}`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateSiteEnablement: async (request: UpdateSiteEnablementRequest): Promise<Carrier> => {
      const { carrierId, from, siteId, spaceId, to } = request;
      const body: Record<string, unknown> = { siteId, spaceId, from, to };
      const response = await client.execute<PharosApiResponse<Carrier>>(() =>
        client.instance.patch(`/carriers/${carrierId}/update-site-enablement`, body),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchCarriersParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  applyStringFilters(body, params);
  applyArrayFilters(body, params);
  return body;
}

function applyStringFilters(body: Record<string, unknown>, params: SearchCarriersParams): void {
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) body['search'] = trimmedSearch;
  if (params.enabledOnSiteId) body['enabledOnSiteId'] = params.enabledOnSiteId;
  if (params.notEnabledOnSiteId) body['notEnabledOnSiteId'] = params.notEnabledOnSiteId;
  if (params.spaceId) body['spaceId'] = params.spaceId;
  if (params.unreferencedInSpaceId) body['unreferencedInSpaceId'] = params.unreferencedInSpaceId;
}

function applyArrayFilters(body: Record<string, unknown>, params: SearchCarriersParams): void {
  if (params.integrations && params.integrations.length > 0) body['integrations'] = params.integrations;
  if (params.types && params.types.length > 0) body['types'] = params.types;
}
